const ethers = require("ethers");
const BN = require("bignumber.js");
const { swapPathWithPreferred, quoteWithRouter, getNetworkConstants, toBN, toEthersBN } = require("../lib/utils");

const StrategyMasterChef = require("../contracts/StrategyMasterChefV2.json");
const UniswapRouterV2 = require("../contracts/IUniswapV2Router02.json");
const UniswapPairABI = require("../contracts/IUniswapV2Pair.json");
const IERC20ABI = require("../contracts/IERC20.json");

const SLIPPAGE_TOLERANCE_PERCENTAGE = 5;
const MAX_AMOUNT = toEthersBN(BN(2).pow(256).minus(1));
const ADDRESS_ZERO = "0x0000000000000000000000000000000000000000";

module.exports = async ({
  positionAddress,
  liquidityOwner,
  strategist,
  provider,
  protocol,
  farm,
  abi
}) => {

  provider = provider || liquidityOwner?.provider || strategist?.provider;
  const NETWORK_CONSTANTS = await getNetworkConstants(provider);
  const PROTOCOL_CONSTANTS = NETWORK_CONSTANTS.PROTOCOLS[protocol];

  const {
    NATIVE_TOKEN,
    TOKEN_SYMBOL_TO_ADDRESS,
    TOKEN_ADDRESS_TO_SYMBOL,
    TOKEN_ADDRESS_TO_DECIMALS,
    PREFERRED_SWAP_PATH_TOKEN_ADDRESS
  } = NETWORK_CONSTANTS;
  const FARM_CONSTANTS = PROTOCOL_CONSTANTS.FARMS[farm];
  const FARM_ADDRESS = FARM_CONSTANTS.ADDRESS;

  const {
    PAIR_SYMBOL_TO_PAIR_ADDRESS,
    PAIR_ADDRESS_TO_REWARDS_POOL,
    PAIR_ADDRESS_TO_REWARDS_LENGTH,
    PAIR_ADDRESS_TO_SUSHIBAR_REWARDS,
    PAIR_ADDRESS_TO_UNWRAPPED_SUSHIBAR_REWARDS,
  } = FARM_CONSTANTS;

  const USD_ORACLE_TOKEN = NETWORK_CONSTANTS.USD_ORACLE_TOKEN || TOKEN_SYMBOL_TO_ADDRESS.USDT || TOKEN_SYMBOL_TO_ADDRESS.USDC;
  const position = new ethers.Contract(positionAddress, abi || StrategyMasterChef.abi, provider);
  const router = new ethers.Contract(PROTOCOL_CONSTANTS.UNIV2_ROUTER_ADDRESS, UniswapRouterV2.abi, provider);
  const IERC20 = new ethers.Contract(ADDRESS_ZERO, IERC20ABI.abi, provider);

  const addressA = await position.tokenA();
  const addressB = await position.tokenB();

  const tokenA = new ethers.Contract(addressA, IERC20ABI.abi, provider);
  const tokenB = new ethers.Contract(addressB, IERC20ABI.abi, provider);

  const decimalsA = TOKEN_ADDRESS_TO_DECIMALS[addressA.toLowerCase()];
  const decimalsB = TOKEN_ADDRESS_TO_DECIMALS[addressB.toLowerCase()];

  const symbolA = TOKEN_ADDRESS_TO_SYMBOL[addressA.toLowerCase()];
  const symbolB = TOKEN_ADDRESS_TO_SYMBOL[addressB.toLowerCase()];

  const pairKey = [symbolA, symbolB].join("_");

  const LPT_ADDRESS = PAIR_SYMBOL_TO_PAIR_ADDRESS[pairKey];
  const REWARDS_POOL = PAIR_ADDRESS_TO_REWARDS_POOL[LPT_ADDRESS];
  const IUniswapV2Pair = new ethers.Contract(LPT_ADDRESS, UniswapPairABI.abi, provider);

  const SUSHIBAR_REWARDS = PAIR_ADDRESS_TO_SUSHIBAR_REWARDS && PAIR_ADDRESS_TO_SUSHIBAR_REWARDS[LPT_ADDRESS] || Array(PAIR_ADDRESS_TO_REWARDS_LENGTH).fill(false);
  const UNWRAPPED_SUSHIBAR_REWARDS = PAIR_ADDRESS_TO_UNWRAPPED_SUSHIBAR_REWARDS && PAIR_ADDRESS_TO_UNWRAPPED_SUSHIBAR_REWARDS[LPT_ADDRESS] || [];

  const getAccruedRewardsAsToken = async(token, ...args) => {
    const [rewardAddresses, rewardAmount] = await position.getAccruedRewards(...args);
    const rewardsSymbol = {};
    for (let i = 0; i < rewardAddresses.length; i++) {
      const rewardTokenAddress = rewardAddresses[i];
      const amount = toBN(rewardAmount[i]);
      if (amount.gt(0)) {
        const swapPath = swapPathWithPreferred(rewardTokenAddress, token, PREFERRED_SWAP_PATH_TOKEN_ADDRESS);
        const rewardsInToken = await quoteWithRouter(router, swapPath, amount, SLIPPAGE_TOLERANCE_PERCENTAGE);
        rewardsSymbol[TOKEN_ADDRESS_TO_SYMBOL[rewardTokenAddress.toLowerCase()]] = rewardsInToken;
      } else {
        rewardsSymbol[TOKEN_ADDRESS_TO_SYMBOL[rewardTokenAddress.toLowerCase()]] = BN(0);
      }
    }
    return rewardsSymbol;
  };

  const getIdleBalance = async () => {
    const [balanceA, balanceB] = await Promise.all([
      tokenA.balanceOf(positionAddress),
      tokenB.balanceOf(positionAddress),
    ]);

    return {
      [symbolA]: toBN(balanceA),
      [symbolB]: toBN(balanceB)
    };
  };

  const getAccruedRewardsAsUSD = async(...args) => {
    const accruedRewardsAsUsd = await getAccruedRewardsAsToken(USD_ORACLE_TOKEN, ...args);
    return Object.entries(accruedRewardsAsUsd)
      .reduce((map, [symbol, amount]) => ({
        ...map,
        [symbol]: amount.scaleDown(TOKEN_ADDRESS_TO_DECIMALS[USD_ORACLE_TOKEN])
      }), {});
  };

  const getAccruedRewardsAsNative = async(...args) => getAccruedRewardsAsToken(NATIVE_TOKEN, ...args);

  const getAccruedRewards = async(...args) => {
    const [rewardAddresses, rewardAmount] = await position.getAccruedRewards(...args);
    const rewardsSymbol = {};
    for (let i = 0; i < rewardAddresses.length; i++) {
      const rewardTokenAddress = rewardAddresses[i];
      const amount = toBN(rewardAmount[i]);
      rewardsSymbol[TOKEN_ADDRESS_TO_SYMBOL[rewardTokenAddress.toLowerCase()]] = amount;
    }
    return rewardsSymbol;
  };

  const getLptBalance = async (...args) => {
    const depositedLpts = await position.getDepositedLpts(...args);
    return toBN(depositedLpts);
  };

  const getLptTotalSupply = async (...args) => {
    const totalSupply = await IUniswapV2Pair.totalSupply(...args);
    return toBN(totalSupply);
  };

  const getUnderlyingBalance = async (...args) => {
    const lpts = await getLptBalance(...args);
    const totalSupply = await getLptTotalSupply(...args);
    const [reserveA, reserveB] = await IUniswapV2Pair.getReserves(...args);

    const amountA = toBN(reserveA).times(lpts).div(totalSupply);
    const amountB = toBN(reserveB).times(lpts).div(totalSupply);

    return {
      [symbolA]: amountA,
      [symbolB]: amountB
    };
  };

  const getLptSupplyInPool = async () => {
    const lptSupplyInPool = await IERC20.attach(LPT_ADDRESS).balanceOf(FARM_ADDRESS || REWARDS_POOL);
    return toBN(lptSupplyInPool);
  };

  const getDivergence = async () => {
    const currentUnderlyingBalance = await getUnderlyingBalance();
    const initialPositionSupply = await getInitialSupply();

    const initialPositionValueInTokenB = initialPositionSupply[symbolB].times(2);
    const currentPositionValueInTokenB = currentUnderlyingBalance[symbolB].times(2);

    const divergence = currentPositionValueInTokenB.div(initialPositionValueInTokenB).minus(1);
    return divergence;
  };

  const getClaimableFees = async () => {
    const claimableFees = await position.getClaimableFees();
    return toBN(claimableFees);
  };

  const claimFees = async ({ gasPrice, gasLimit } = {}, ...args) => {
    const lpts = await getClaimableFees();

    const totalSupply = await getLptTotalSupply(...args);
    const [reserveA, reserveB] = await IUniswapV2Pair.getReserves(...args);

    const amountA = toBN(reserveA).times(lpts).div(totalSupply);
    const amountB = toBN(reserveB).times(lpts).div(totalSupply);

    const minAmountA = amountA.times(100 - SLIPPAGE_TOLERANCE_PERCENTAGE).div(100);
    const minAmountB = amountB.times(100 - SLIPPAGE_TOLERANCE_PERCENTAGE).div(100);
    const transaction = await position.connect(strategist).functions.claimFees(
      toEthersBN(lpts),
      toEthersBN(minAmountA),
      toEthersBN(minAmountB),
      { gasPrice, gasLimit }
    );
    return transaction.wait();
  };

  const getAccumulatedFees = async () => {
    const accumulatedFees = await position.connect(strategist).lptFeesAccumulated();
    return accumulatedFees;
  };

  const getInitialSupply = async () => {
    const tokenASupplied = await position.tokenASupplied();
    const tokenARemoved = await position.tokenARemoved();

    const tokenBSupplied = await position.tokenBSupplied();
    const tokenBRemoved = await position.tokenBRemoved();

    return {
      [symbolA]: toBN(tokenASupplied).minus(toBN(tokenARemoved)),
      [symbolB]: toBN(tokenBSupplied).minus(toBN(tokenBRemoved)),
    };
  };

  const getPairLiquidity = async () => {
    const [reserveA, reserveB] = await IUniswapV2Pair.getReserves();
    return {
      [symbolA]: toBN(reserveA),
      [symbolB]: toBN(reserveB)
    };
  };

  const getRewardTokenAddresses = async () => {
    const [addresses] = await position.getAccruedRewards(false);
    return addresses;
  };

  const buildApproveTokensTransaction = ({ tokens, spenders }, { gasPrice, gasLimit } = {}) => (
    position.populateTransaction.approveTokens(tokens, spenders, Array(spenders.length).fill(MAX_AMOUNT), { gasPrice, gasLimit })
  );

  const approveTokens = async ({ tokens, spenders }, { gasPrice, gasLimit, wait }) => {
    const unsignedTransaction = await buildApproveTokensTransaction(tokens, spenders, { gasPrice, gasLimit });
    const tx = await liquidityOwner.sendTransaction(unsignedTransaction);
    if (wait) await tx.wait();
    return tx;
  };

  const approveTokensForAssembly = async ({ gasPrice, gasLimit } = {}) => {
    const rewardTokenAddresses = await getRewardTokenAddresses();

    const tokens = [
      addressA,
      addressB,
      LPT_ADDRESS,
      LPT_ADDRESS,
    ];

    const spenders = [
      router.address,
      router.address,
      router.address,
      FARM_ADDRESS || REWARDS_POOL,
    ];

    for (let i = 0; i < rewardTokenAddresses.length; i++) {
      let rewardTokenAddress = rewardTokenAddresses[i];
      if (SUSHIBAR_REWARDS[i]) {
        rewardTokenAddress = UNWRAPPED_SUSHIBAR_REWARDS[i];
      }
      tokens.push(rewardTokenAddress);
      spenders.push(router.address);
    }

    return approveTokens(tokens, spenders, { gasPrice, gasLimit });
  };

  const buildAssembleTransaction = ({ amountA, amountB }, opts = {}) => (
    position.populateTransaction.assemble(
      toEthersBN(amountA),
      toEthersBN(amountB),
      opts
    )
  );

  const assemble = async ({ amountA, amountB }, opts = {}) => {
    const unsignedTransaction = await buildAssembleTransaction({ amountA, amountB }, opts);
    const transaction = await strategist.sendTransaction(unsignedTransaction);
    return transaction.wait();
  };

  const buildDisassembleTransaction = async (opts = {}) => {
    const underlyingBalance = await getUnderlyingBalance();
    const depositedLpt = await getLptBalance();
    const minAmountA = underlyingBalance[symbolA].times(100 - SLIPPAGE_TOLERANCE_PERCENTAGE).div(100);
    const minAmountB = underlyingBalance[symbolB].times(100 - SLIPPAGE_TOLERANCE_PERCENTAGE).div(100);

    return position.populateTransaction.disassemble(
      toEthersBN(depositedLpt),
      toEthersBN(minAmountA),
      toEthersBN(minAmountB),
      opts
    );

  };

  const buildWithdrawTransaction = async ({ asset, amount, to }, opts = {}) => position.populateTransaction.withdraw(
    asset,
    toEthersBN(amount),
    to,
    opts
  );

  const buildDisassembleAndWithdrawTransaction = async ({ to }, opts = {}) => {
    const underlyingBalance = await getUnderlyingBalance();
    const minAmountA = underlyingBalance[symbolA].times(100 - SLIPPAGE_TOLERANCE_PERCENTAGE).div(100);
    const minAmountB = underlyingBalance[symbolB].times(100 - SLIPPAGE_TOLERANCE_PERCENTAGE).div(100);
    const depositedLpt = await getLptBalance();

    return position.populateTransaction.disassembleAndWithdraw(
      toEthersBN(depositedLpt),
      toEthersBN(minAmountA),
      toEthersBN(minAmountB),
      to,
      opts
    );
  };

  const disassembleAndWithdraw = async ({ to }, opts) => {
    const unsignedTransaction = await buildDisassembleAndWithdrawTransaction({ to }, opts);
    const transaction = await liquidityOwner.sendTransaction(unsignedTransaction);
    return transaction.wait();
  };

  const disassemble = async (opts = {}) => {
    const unsignedTransaction = await buildDisassembleTransaction(opts);
    const transaction = await strategist.sendTransaction(unsignedTransaction);
    return transaction.wait();
  };

  const buildArgsForHarvest = async () => {
    const rewardsSwapMinOuts = [];

    const rewards = await getAccruedRewards(true);
    const rewardSymbols = Object.keys(rewards);

    const rewardsSwapPaths = await position.getRewardsSwapPaths();

    for (let i = 0; i < rewardSymbols.length; i++) {
      const rewardSwapMinOuts = [];
      const unwrappedSushibar = UNWRAPPED_SUSHIBAR_REWARDS[i] ;

      const rewardSymbol = rewardSymbols[i];
      let amount = rewards[rewardSymbol];
      let rewardTokenAddress = TOKEN_SYMBOL_TO_ADDRESS[rewardSymbol];

      if (unwrappedSushibar) {
        amount = await position.getUnderlyingSushibarBalance(rewardTokenAddress, unwrappedSushibar, toEthersBN(amount));
        amount = toBN(amount);
        rewardTokenAddress = unwrappedSushibar;
      }

      const rewardForTokenA = amount.div(2);
      const rewardForTokenB = amount.div(2);

      const swapPathTokenA = rewardsSwapPaths[i][0];
      const swapPathTokenB = rewardsSwapPaths[i][1];

      const minOutSwapA = await quoteWithRouter(router, swapPathTokenA, rewardForTokenA, SLIPPAGE_TOLERANCE_PERCENTAGE);
      const minOutSwapB = await quoteWithRouter(router, swapPathTokenB, rewardForTokenB, SLIPPAGE_TOLERANCE_PERCENTAGE);

      rewardSwapMinOuts.push(toEthersBN(minOutSwapA));
      rewardSwapMinOuts.push(toEthersBN(minOutSwapB));

      rewardsSwapMinOuts.push(rewardSwapMinOuts);
    }

    const args = [rewardsSwapMinOuts];

    return args;
  };

  const buildHarvestTransaction = async () => {
    const harvestArgs = await buildArgsForHarvest();
    return position.populateTransaction.harvest(...harvestArgs);
  };

  const harvest = async ({
    estimateGasOnly,
    gasPrice,
    gasLimit
  } = {}) => {
    const args = await buildArgsForHarvest();
    if (estimateGasOnly) {
      const gas = await position.connect(strategist).estimateGas.harvest(...args);
      return toBN(gas);
    } else {
      return position.connect(strategist).functions.harvest(...args, { gasLimit, gasPrice });
    }
  };

  const printBalances = async () => {
    const [eth, amountA, amountB, pendingRewards, lpt, depositedLpt] = await Promise.all([
      provider.getBalance(positionAddress),
      IERC20.attach(addressA).balanceOf(positionAddress),
      IERC20.attach(addressB).balanceOf(positionAddress),
      getAccruedRewards(false),
      IERC20.attach(LPT_ADDRESS).balanceOf(positionAddress),
      getLptBalance(),
    ]);
    const table = [];
    const row = {};
    row["ETH"] = eth.toString();
    row[symbolA] = amountA.toString();
    row[symbolB] = amountB.toString();
    row["PENDING REWARDS"] = Object.entries(pendingRewards).map((r) => r.join(": ")).join(", ");
    row["LPT"] = lpt.toString();
    row["DEPOSITED LPT"] = depositedLpt.toString();
    table.push(row);
    console.table(table);
  };

  return {
    getLptBalance,
    getDivergence,
    getAccruedRewards,
    getAccruedRewardsAsUSD,
    getAccruedRewardsAsNative,
    getInitialSupply,
    getPairLiquidity,
    approveTokens,
    approveTokensForAssembly,
    getUnderlyingBalance,
    getLptTotalSupply,
    getLptSupplyInPool,
    buildArgsForHarvest,
    getRewardTokenAddresses,
    assemble,
    buildApproveTokensTransaction,
    buildAssembleTransaction,
    buildDisassembleTransaction,
    buildHarvestTransaction,
    disassemble,
    buildWithdrawTransaction,
    harvest,
    printBalances,
    claimFees,
    disassembleAndWithdraw,
    buildDisassembleAndWithdrawTransaction,
    getClaimableFees,
    getAccumulatedFees,
    networkConstants: NETWORK_CONSTANTS,
    address: positionAddress,
    pairAddress: LPT_ADDRESS,
    addressA,
    addressB,
    symbolA,
    symbolB,
    position,
    decimalsA,
    decimalsB,
    provider,
    router,
    getIdleBalance,
    USD_ORACLE_TOKEN,
    rewardsPool: REWARDS_POOL
  };
};
